import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AlertTriangle, MapPin, Shield, Plus } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export const Route = createFileRoute("/emergency")({
  head: () => ({ meta: [{ title: "Xamseen — Emergency Alerts" }] }),
  component: EmergencyPage,
});

const severityColor: Record<string, string> = {
  info: "bg-muted text-foreground",
  warning: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
  urgent: "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  critical: "bg-destructive text-destructive-foreground",
};

function EmergencyPage() {
  const [alerts, setAlerts] = useState<any[]>([]);
  useEffect(() => {
    supabase.from("emergency_alerts").select("*").eq("active", true).order("created_at", { ascending: false }).limit(50)
      .then(({ data }) => setAlerts(data ?? []));
  }, []);

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Emergency Alerts</h1>
            <p className="text-muted-foreground">Verified safety information from your communities.</p>
          </div>
          <Button variant="destructive"><Plus className="mr-2 h-4 w-4" />Report alert</Button>
        </header>

        {alerts.length === 0 ? (
          <Card className="p-12 text-center">
            <Shield className="mx-auto mb-3 h-10 w-10 text-secondary" />
            <p className="font-medium">No active alerts</p>
            <p className="text-sm text-muted-foreground">Your area is calm right now.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => (
              <Card key={a.id} className="overflow-hidden">
                <div className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${severityColor[a.severity]}`}>
                  <AlertTriangle className="h-4 w-4" />
                  <span className="uppercase tracking-wider">{a.severity}</span>
                  {a.verified && <Badge variant="outline" className="ml-auto">Verified</Badge>}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold">{a.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{a.body}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {a.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location_name}</span>}
                    <span>{formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
